import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, TrendingUp, TrendingDown, DollarSign, ShoppingBag, Users, AlertCircle, RefreshCw } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { fmtMoney } from '../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => fmtMoney(n, 0);

const fmtShort = (n) => {
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
};

const COLORS = ['#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#8b5cf6', '#f97316'];

function MetricCard({ label, value, sub, icon: Icon, color = 'zinc', positive }) {
  const colorMap = {
    zinc:   'bg-zinc-50  border-zinc-200  text-zinc-900',
    green:  'bg-green-50 border-green-200 text-green-800',
    amber:  'bg-amber-50 border-amber-200 text-amber-800',
    red:    'bg-red-50   border-red-200   text-red-800',
    blue:   'bg-blue-50  border-blue-200  text-blue-800',
  };
  return (
    <div className={`rounded-xl border p-4 flex flex-col gap-1 ${colorMap[color]}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium opacity-70 uppercase tracking-wide">{label}</span>
        {Icon && <Icon className="w-4 h-4 opacity-50" />}
      </div>
      <span className="text-2xl font-bold">{value}</span>
      {sub && <span className="text-xs opacity-60">{sub}</span>}
      {positive !== undefined && (
        <span className={`text-xs flex items-center gap-0.5 ${positive ? 'text-green-600' : 'text-red-500'}`}>
          {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {sub}
        </span>
      )}
    </div>
  );
}

function isoToday() {
  return new Date().toISOString().split('T')[0];
}

function isoWeekAgo() {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return d.toISOString().split('T')[0];
}

export function PropietarioDashboard() {
  const navigate = useNavigate();
  const [fechaInicio, setFechaInicio] = useState(isoWeekAgo);
  const [fechaFin, setFechaFin] = useState(isoToday);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: res } = await axios.get(`${API}/propietario/dashboard`, {
        params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin },
      });
      setData(res);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  }, [fechaInicio, fechaFin]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const resumen = data?.resumen || {};
  const porDia = (data?.por_dia || []).map(d => {
    const total_venta = (d.venta_efectivo || 0) + (d.venta_credito || 0);
    const total_costo = (d.compra || 0) + (d.gastos || 0);
    return { ...d, total_venta, total_costo, utilidad: total_venta - total_costo };
  });
  const porPunto = (data?.por_punto || []).map(p => ({ ...p, total_venta: p.venta || 0 }));

  const utilidad = resumen.utilidad_bruta ?? ((resumen.total_venta || 0) - (resumen.total_compra || 0) - (resumen.total_gastos || 0));
  const pctUtilidad = resumen.total_venta > 0 ? ((utilidad / resumen.total_venta) * 100).toFixed(1) : '0';

  const pieData = [
    { name: 'Efectivo', value: (resumen.total_venta_efectivo || 0) },
    { name: 'Crédito', value: resumen.total_venta_credito || 0 },
    { name: 'Abonos', value: resumen.total_abonos || 0 },
  ].filter(d => d.value > 0);

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-zinc-900">Dashboard Propietario</h1>
            <p className="text-xs text-zinc-500">Resumen financiero del negocio</p>
          </div>
          <button
            onClick={fetchDashboard}
            disabled={loading}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg text-zinc-500"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="px-4 py-4 space-y-5 pb-24">
        {/* Date range */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <p className="text-sm font-medium text-zinc-600 mb-3">Rango de fechas</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Desde</label>
              <input
                type="date"
                value={fechaInicio}
                max={fechaFin}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm font-medium"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Hasta</label>
              <input
                type="date"
                value={fechaFin}
                min={fechaInicio}
                max={isoToday()}
                onChange={(e) => setFechaFin(e.target.value)}
                className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm font-medium"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {loading && !data && (
          <div className="flex items-center justify-center py-20">
            <span className="w-8 h-8 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />
          </div>
        )}

        {data && (
          <>
            {/* Metric cards */}
            <div className="grid grid-cols-2 gap-3">
              <MetricCard
                label="Total Compra"
                value={fmt(resumen.total_compra)}
                sub={`${resumen.total_pollos || 0} pollos`}
                icon={ShoppingBag}
                color="amber"
              />
              <MetricCard
                label="Total Venta"
                value={fmt(resumen.total_venta)}
                sub="ventas del período"
                icon={DollarSign}
                color="green"
              />
              <MetricCard
                label="Venta Crédito"
                value={fmt(resumen.total_venta_credito)}
                sub="pendiente de cobro"
                icon={Users}
                color="blue"
              />
              <MetricCard
                label="Abonos"
                value={fmt(resumen.total_abonos)}
                sub="cobrado de créditos"
                icon={TrendingUp}
                color="zinc"
              />
              <MetricCard
                label="Gastos"
                value={fmt(resumen.total_gastos)}
                icon={TrendingDown}
                color="red"
              />
              <MetricCard
                label="Cuentas por Cobrar"
                value={fmt(resumen.cuentas_por_cobrar)}
                sub="saldo total clientes"
                icon={AlertCircle}
                color="zinc"
              />
            </div>

            {/* Utilidad bruta */}
            <div className={`rounded-xl border p-4 ${utilidad >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">Utilidad Bruta</p>
                  <p className={`text-3xl font-bold mt-0.5 ${utilidad >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {fmt(utilidad)}
                  </p>
                </div>
                <div className={`text-right text-sm font-medium ${utilidad >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  <p className="text-2xl font-bold">{pctUtilidad}%</p>
                  <p className="text-xs opacity-70">del total venta</p>
                </div>
              </div>
            </div>

            {/* Comparativo diario */}
            {porDia.length > 1 && (
              <div className="bg-white rounded-xl border border-zinc-200 p-4">
                <p className="text-sm font-bold text-zinc-800 mb-1">Comparativo diario</p>
                <p className="text-xs text-zinc-400 mb-3">Ventas vs Compra+Gastos = Utilidad bruta</p>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={porDia} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                    <XAxis
                      dataKey="fecha"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v) => v.slice(5)}
                    />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtShort} width={48} />
                    <Tooltip
                      formatter={(v, name) => {
                        const labels = { total_venta: 'Ventas', total_costo: 'Compra+Gastos', utilidad: 'Utilidad' };
                        return [fmt(v), labels[name] || name];
                      }}
                      labelFormatter={(l) => `Fecha: ${l}`}
                    />
                    <Legend
                      iconSize={10}
                      wrapperStyle={{ fontSize: 11 }}
                      formatter={(v) => ({ total_venta: 'Ventas', total_costo: 'Compra+Gastos', utilidad: 'Utilidad' }[v] || v)}
                    />
                    <ReferenceLine y={0} stroke="#d4d4d8" strokeDasharray="3 3" />
                    <Line type="monotone" dataKey="total_venta"  stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="total_costo"  stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="utilidad"     stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 4" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Ventas por punto de venta */}
            {porPunto.length > 0 && (
              <div className="bg-white rounded-xl border border-zinc-200 p-4">
                <p className="text-sm font-bold text-zinc-800 mb-3">Ventas por punto de venta</p>
                <ResponsiveContainer width="100%" height={Math.max(180, porPunto.length * 50)}>
                  <BarChart
                    data={porPunto}
                    layout="vertical"
                    margin={{ top: 0, right: 0, left: 4, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                    <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={fmtShort} />
                    <YAxis type="category" dataKey="nombre" tick={{ fontSize: 11 }} width={90} />
                    <Tooltip formatter={(v) => [fmt(v), 'Venta']} />
                    <Bar dataKey="total_venta" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Distribución cobro */}
            {pieData.length > 0 && (
              <div className="bg-white rounded-xl border border-zinc-200 p-4">
                <p className="text-sm font-bold text-zinc-800 mb-3">Distribución de cobro</p>
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width="50%" height={160}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={70}
                        dataKey="value"
                        paddingAngle={2}
                      >
                        {pieData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-col gap-2">
                    {pieData.map((item, i) => (
                      <div key={item.name} className="flex items-center gap-2 text-sm">
                        <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: COLORS[i] }} />
                        <span className="text-zinc-700">{item.name}</span>
                        <span className="font-bold text-zinc-900 ml-auto pl-4">{fmt(item.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
}
